import { c as _c } from "react/compiler-runtime";
import { createContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { usePathname, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { AuthLoading } from '@app/auth/_components/AuthLoading';
import { Unauthorized } from '@app/auth/_components/Unauthorized';
import { SoftphoneSession } from '@components/contacts/sessions/SoftphoneSession';
import { pattern } from '@endurancewarranty/lib';
import { useQueryGetMyUser } from '@hooks/query/users/useQueryGetMyUser';
import { useIsPopupWindow } from '@hooks/useIsPopupWindow';
import { signInAction } from '@lib/actions';
import { SoftphoneConnectionBehaviorEnum, TimeEnum } from '@lib/enums';
import { appAtom } from '@states/app/appAtom';
import { useSetJotaiAtom } from '@states/jotai/hooks';
import { softphoneAtom } from '@states/softphone/softphoneAtom';
import type { TDashboardUx } from '@lib/types/generated/TPermission';
import type { TUser } from '@models/authorization/TUser';
import type { FC, ReactNode } from 'react';
const {
  match,
  isMatching,
  P
} = pattern;

/**
 * Value exposed by UserContext.
 * Guaranteed to contain user once provider renders children.
 **/
export type TUserState = Readonly<{
  user: TUser;
}>;

/**
 * Provides authenticated user.
 */
export const UserContext = createContext<TUserState | null>(null);
type Props = {
  children: ReactNode;
};

/**
 * Provider for post-auth setup.
 *
 * Lifecycle:
 * 1. Loading.
 * 2. Auth check, redirects unauthenticated users.
 * 3. User fetch, calls API to hydrate full `TUser`.
 * 4. Role setup, derive dashboard visibility + softphone behavior from roles.
 * 5. Ready.
 */
export const UserContextProvider: FC<Props> = t0 => {
  const $ = _c(49);
  const {
    children
  } = t0;
  const {
    data: session,
    status,
    update
  } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const isPopupWindow = useIsPopupWindow();
  const [isAppReady, setIsAppReady] = useState(false);
  const setAppAtom = useSetJotaiAtom(appAtom);
  const setSoftphoneAtom = useSetJotaiAtom(softphoneAtom);
  const [refreshRetried, setRefreshRetried] = useState(false);
  let t1;
  if ($[0] !== refreshRetried || $[1] !== session?.error || $[2] !== update) {
    t1 = () => {
      if (session?.error === "RefreshTokenError" && !refreshRetried) {
        setRefreshRetried(true);
        update().catch(_temp);
      }
      if (session?.error == null) {
        setRefreshRetried(false);
      }
    };
    $[0] = refreshRetried;
    $[1] = session?.error;
    $[2] = update;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = session?.error;
  let t3;
  if ($[4] !== refreshRetried || $[5] !== t2 || $[6] !== update) {
    t3 = [t2, refreshRetried, update];
    $[4] = refreshRetried;
    $[5] = t2;
    $[6] = update;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t1, t3);
  const isAuthenticated = status === "authenticated";
  let t4;
  if ($[8] !== isAuthenticated) {
    t4 = {
      enabled: isAuthenticated
    };
    $[8] = isAuthenticated;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError
  } = useQueryGetMyUser(t4);
  let t5;
  let t6;
  if ($[10] !== isPopupWindow || $[11] !== setAppAtom || $[12] !== setSoftphoneAtom || $[13] !== user) {
    t5 = () => {
      if (user == null || isPopupWindow) {
        setSoftphoneAtom(_temp2);
        setIsAppReady(true);
        return;
      }
      const hasRole = key => user.roles.some(isMatching({
        roleKey: key
      }));
      const isSuperAdmin = hasRole("super-admin");
      const isBusinessAdmin = hasRole("business-admin");
      const isReadOnly = hasRole("read-only");
      const isSalesLeaderRole = user.roles.some(isMatching({
        roleKey: P.union("sales-manager", "sales-director")
      }));
      const isCustomerServiceManagerRole = hasRole("customer-service-manager");
      const isCustomerServiceRole = hasRole("customer-service");
      const isQualityAssuranceRole = hasRole("quality-assurance");
      const isClaimsAdjuster = hasRole("claims-adjuster");
      const isBillingRole = user.roles.some(isMatching({
        roleKey: P.string.startsWith("billing")
      }));
      const isPaymentsManager = hasRole("payments-manager");
      const isRetentionManager = hasRole("retention-manager");
      const isResolutions = hasRole("resolutions");
      const items = [...new Set(user.v2?.roles.map(_temp3) ?? [])] as TDashboardUx[];
      const connectionBehavior = match({
        isReadOnly,
        isSalesLeaderRole,
        isSuperAdmin,
        isBusinessAdmin,
        isCustomerServiceManagerRole,
        isCustomerServiceRole,
        isQualityAssuranceRole,
        isClaimsAdjuster,
        isBillingRole,
        isPaymentsManager,
        isRetentionManager,
        isResolutions
      }).with({
        isSalesLeaderRole: true
      }, {
        isCustomerServiceManagerRole: true
      }, {
        isSuperAdmin: true
      }, _temp4).with({
        isReadOnly: true
      }, {
        isBusinessAdmin: true
      }, {
        isQualityAssuranceRole: true
      }, {
        isClaimsAdjuster: true
      }, {
        isBillingRole: true
      }, {
        isPaymentsManager: true
      }, {
        isRetentionManager: true
      }, {
        isResolutions: true
      }, _temp5).otherwise(_temp6);
      setSoftphoneAtom(curr => ({
        ...curr,
        connectionBehavior
      }));
      setAppAtom(curr_0 => ({
        ...curr_0,
        dashboard: items
      }));
      setIsAppReady(true);
    };
    t6 = [setSoftphoneAtom, setAppAtom, isPopupWindow, user];
    $[10] = isPopupWindow;
    $[11] = setAppAtom;
    $[12] = setSoftphoneAtom;
    $[13] = user;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[16] !== pathname || $[17] !== router || $[18] !== status) {
    t7 = () => {
      if (status === "unauthenticated" && !pathname?.startsWith("/auth")) {
        const params = new URLSearchParams();
        if (pathname && pathname !== "/") {
          const search = window.location.search;
          params.set("callbackUrl", search ? `${pathname}${search}` : pathname);
        }
        const query = params.toString();
        const target = query ? `/auth?${query}` : "/auth";
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Redirecting unauthenticated user to /auth",
          level: "warning",
          data: {
            from: pathname,
            to: target
          }
        });
        router.replace(target);
      }
    };
    t8 = [status, pathname, router];
    $[16] = pathname;
    $[17] = router;
    $[18] = status;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[21] !== user) {
    t9 = () => {
      if (user != null) {
        const sentryUser = {
          id: user.azureAdId,
          email: user.emailAddress.toLowerCase()
        };
        Sentry.setUser(sentryUser);
      }
      return _temp7;
    };
    t10 = [user];
    $[21] = user;
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[24] !== userError) {
    t11 = () => {
      if (userError) {
        Sentry.captureException(userError);
      }
    };
    t12 = [userError];
    $[24] = userError;
    $[25] = t11;
    $[26] = t12;
  } else {
    t11 = $[25];
    t12 = $[26];
  }
  useEffect(t11, t12);
  if (status === "loading") {
    let t13;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <AuthLoading reason="Authenticating..." />;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    return t13;
  }
  let t13;
  bb0: {
    if (!pathname) {
      t13 = undefined;
      break bb0;
    }
    const search_0 = typeof window !== "undefined" ? window.location.search : "";
    t13 = search_0 ? `${pathname}${search_0}` : pathname;
  }
  const fullCallbackUrl = t13;
  if (session?.error === "RefreshTokenError") {
    if (!refreshRetried) {
      let t14;
      if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = <AuthLoading reason="Authenticating..." />;
        $[28] = t14;
      } else {
        t14 = $[28];
      }
      return t14;
    }
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Session has RefreshTokenError \u2014 showing sign-in prompt",
      level: "warning",
      data: {
        pathname,
        hasAccessToken: !!session.accessToken
      }
    });
    let t14;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <>Your session has expired.<br />Please sign in again to continue.</>;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    let t15;
    if ($[30] !== fullCallbackUrl) {
      t15 = <Unauthorized action={signInAction} buttonLabel="Sign in" callbackUrl={fullCallbackUrl} message={t14} />;
      $[30] = fullCallbackUrl;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    return t15;
  }
  if (!isAuthenticated) {
    if (pathname?.startsWith("/auth")) {
      let t14;
      if ($[32] !== children) {
        t14 = <>{children}</>;
        $[32] = children;
        $[33] = t14;
      } else {
        t14 = $[33];
      }
      return t14;
    }
    let t14;
    if ($[34] !== fullCallbackUrl) {
      t14 = <AuthLoading reason="Redirecting to login" fallbackAction={signInAction} fallbackCallbackUrl={fullCallbackUrl} fallbackDelayMs={TimeEnum.FiveSeconds} />;
      $[34] = fullCallbackUrl;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    return t14;
  }
  if (isUserLoading) {
    let t14;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <AuthLoading reason="Fetching account" />;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    return t14;
  }
  if (isUserError) {
    let t14;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <>Something went wrong during sign in.<br />Please try again or contact IT if the problem continues.</>;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    let t15;
    if ($[38] !== fullCallbackUrl) {
      t15 = <Unauthorized action={signInAction} buttonLabel="Try again" callbackUrl={fullCallbackUrl} message={t14} />;
      $[38] = fullCallbackUrl;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    return t15;
  }
  if (!isAppReady) {
    let t14;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <AuthLoading reason="Setting up KITT" />;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    return t14;
  }
  const t14 = user as TUser;
  let t15;
  if ($[41] !== t14) {
    t15 = {
      user: t14
    };
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== children || $[44] !== isPopupWindow) {
    t16 = isPopupWindow ? children : <SoftphoneSession>{children}</SoftphoneSession>;
    $[43] = children;
    $[44] = isPopupWindow;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t15 || $[47] !== t16) {
    t17 = <UserContext.Provider value={t15}>{t16}</UserContext.Provider>;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
};
function _temp() {
  Sentry.addBreadcrumb({
    category: "auth",
    message: "Session update() rejected during RefreshTokenError retry",
    level: "warning"
  });
}
function _temp2(current) {
  return {
    ...current,
    connectionBehavior: SoftphoneConnectionBehaviorEnum.DoNotConnect
  };
}
function _temp3(role) {
  return role.dashboardUx;
}
function _temp4() {
  return SoftphoneConnectionBehaviorEnum.OptIn;
}
function _temp5() {
  return SoftphoneConnectionBehaviorEnum.DoNotConnect;
}
function _temp6() {
  return SoftphoneConnectionBehaviorEnum.AutoConnect;
}
function _temp7() {
  return Sentry.setUser(null);
}